import { Css, PreventBrowserScroll, ScrollableParent } from "@homebound/beam";
import { PropsWithChildren } from "react";
import { ZendeskWidget } from "src/components/ZendeskWidget";
import { useLoggedInAppSetup } from "src/hooks/useLoggedInAppSetup";
import { useMatchAny } from "src/hooks/useMatchAny";
import { GlobalNav } from "src/routes/layout/global-nav/GlobalNav";
import { LoggedInPageWrapper } from "src/routes/layout/LoggedInPageWrapper";
import {
  adminPaths,
  developmentPaths,
  librariesPath,
  personalDashboardPaths,
  projectPaths,
  warrantyPaths,
} from "src/routes/routesDef";
import { AppProviders } from "src/routing/components/AppProviders";
import { EnvBanner } from "src/routing/components/EnvBanner";

export type PageLayoutProps = PropsWithChildren;

/**
 * LEGACY ROUTING — logged-in layout for URLs that fall through `Router.tsx` to `App`.
 * Migrated domains use `AppShell` + `components/layouts/` instead. See `src/routing/README.md`.
 */
export function PageLayout(props: PageLayoutProps) {
  // We want two separate top level layouts whether you are in a Project/Development/Libraries specific path or not.
  // This makes styling with the side nav easier if we do not need to nest ScrollableParent containers.
  const hasOwnLayout = useMatchAny([
    projectPaths.project,
    developmentPaths.development,
    adminPaths.admin,
    librariesPath,
    personalDashboardPaths.dashboard,
    // `/change-log/*` is served by the data router (src/routing/domains/changeLog.ts).
    warrantyPaths.details,
  ]);

  const content = hasOwnLayout ? (
    props.children
  ) : (
    <ScrollableParent tagName="main" px={3} xss={Css.bgGray100.$}>
      {props.children}
    </ScrollableParent>
  );

  return (
    <AppProviders>
      <PageLayoutContent>{content}</PageLayoutContent>
    </AppProviders>
  );
}

/** Must sit under `AppProviders` so hooks using `useToast` have ToastProvider. */
function PageLayoutContent(props: PropsWithChildren) {
  // must run inside `AppProviders` (Beam `ToastProvider`) so Apollo
  // error toasts from `useCurrentInternalUserQuery` do not throw "Missing ToastProvider".
  useLoggedInAppSetup();

  return (
    <>
      <ZendeskWidget />
      <PreventBrowserScroll>
        <EnvBanner />
        <GlobalNav />
        <LoggedInPageWrapper>{props.children}</LoggedInPageWrapper>
      </PreventBrowserScroll>
    </>
  );
}
